import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import authStore from '@/store/auth';

// Homepage version. Everyone sees DEFAULT_VERSION; only admins can switch their
// own browser to another version to compare. The choice lives in localStorage
// and is ignored for non-admins, so a stray value never changes what visitors see.
// v2 became the public homepage on 2026-09-26; set this back to 'current' to roll back.
const STORAGE_KEY = 'landing_version_preview';
export const DEFAULT_VERSION = 'v2';
// 'olj' / 'olj2' are the OnlineJobs.ph-focused homepages removed from public in f762386,
// kept here for admin comparison only.
export const LANDING_VERSIONS = ['current', 'v2', 'olj', 'olj2'];

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return LANDING_VERSIONS.includes(v) ? v : DEFAULT_VERSION;
  } catch {
    return DEFAULT_VERSION;
  }
}

// Module-level so the admin page and the homepage share one value.
const preference = ref(readStored());

export function isAdminUser(user) {
  return !!(user && (user.is_superuser || user.account_type === 'admin' || user.admin_profile));
}

export function useLandingVersion() {
  const isAdmin = computed(() => isAdminUser(authStore.state.user));

  const activeVersion = computed(() => (isAdmin.value ? preference.value : DEFAULT_VERSION));

  function setVersion(version) {
    if (!isAdmin.value || !LANDING_VERSIONS.includes(version)) return;
    preference.value = version;
    try {
      localStorage.setItem(STORAGE_KEY, version);
    } catch {
      // storage blocked — the switch still works for this page load
    }
  }

  return { isAdmin, preference, activeVersion, setVersion };
}

// Guest preview: an admin opens /?preview=1 and browses the public site exactly as a
// logged-out visitor sees it (guest sidebar/navbar, footer, guest CTAs). It lasts for
// the browser tab and follows the admin across public pages (Features, Pricing, …);
// the router ends it as soon as they open a page that needs login, or Log In / Sign Up.
const PREVIEW_SESSION_KEY = 'landing_guest_preview';

function readPreviewSession() {
  try {
    return sessionStorage.getItem(PREVIEW_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

const previewSession = ref(readPreviewSession());

function writePreviewSession(on) {
  previewSession.value = on;
  try {
    if (on) sessionStorage.setItem(PREVIEW_SESSION_KEY, '1');
    else sessionStorage.removeItem(PREVIEW_SESSION_KEY);
  } catch {
    // storage blocked — preview still works until the next reload
  }
}

export const startGuestPreview = () => writePreviewSession(true);
export const endGuestPreview = () => writePreviewSession(false);

export function isGuestPreviewActive(user) {
  return previewSession.value && isAdminUser(user);
}

// Public page = not behind login and not a guest-only page (login/register).
export function isPublicRoute(route) {
  return !!route && !route.matched.some((r) => r.meta.requiresAuth || r.meta.requiresGuest);
}

export function useGuestPreview() {
  const route = useRoute();
  const isGuestPreview = computed(
    () => isGuestPreviewActive(authStore.state.user) && isPublicRoute(route),
  );
  return { isGuestPreview };
}
